import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box, Heading } from '@primer/react';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Caption = makeShortcode("Caption");
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Dont = makeShortcode("Dont");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2>{`Get started`}</h2>
    <img width="960" alt="Image showing the two different types of color modes: light mode and dark mode" src="https://user-images.githubusercontent.com/6951037/146927448-cb518377-114a-4ab8-a37e-1eeffc8732c7.png" />
    <p>{`GitHub's UI offers a variety of different color modes. Every pattern in Primer is built to work across all color modes out of the box.`}</p>
    <p>{`When designing product interfaces in Figma, we recommend using light mode. This is best because the Primer Figma components are only available in light mode.
To preview your work in other modes, use the `}<a parentName="p" {...{
        "href": "https://www.figma.com/community/plugin/992128487074360945/Change-Color-Mode"
      }}>{`Figma color mode plugin`}</a>{`.`}</p>
    <h2>{`How to use color for product UI`}</h2>
    <p>{`Primer delivers colors in the form of `}<a parentName="p" {...{
        "href": "https://primer.style/primitives/colors"
      }}>{`design tokens`}</a>{`. Design tokens are a layer of abstraction that allows better maintainability, consistency and easy theming.`}</p>
    <p>{`For example use `}<inlineCode parentName="p">{`bg-default`}</inlineCode>{` for the background of the page and `}<inlineCode parentName="p">{`fg-default`}</inlineCode>{` for the text color. If the user changes to dark mode, the underlying color that those tokens reference change, but the token names stay the same.`}</p>
    <img width="960" alt="Same component shown in light mode and dark mode color palettes" src="https://user-images.githubusercontent.com/813754/187436947-875dbf46-1b26-42da-b96f-e7cbe315f0eb.png" />
    <h3>{`Color design tokens are grouped based on their purpose:`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Presentational:`}</strong>{` To represent a color. For example, the color steps in the Primer scale are named by color and lightness, such as `}<inlineCode parentName="li">{`scale.blue.5`}</inlineCode>{`. These design tokens don't support color modes.`}</li>
      <li parentName="ul"><strong parentName="li">{`Functional:`}</strong>{` To convey a meaning or a state. For example, from a functional perspective the color green is used to reinforce positive messaging. In a functional system, green design tokens are named with the suffix `}<inlineCode parentName="li">{`.success`}</inlineCode>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`Component:`}</strong>{` To represent a specific use case. For example, `}<inlineCode parentName="li">{`button.bg`}</inlineCode>{` references from the system to be used as the background of a button component.`}</li>
    </ul>
    <p>{`As the system grows, it will provide more APIs that fit different color needs. Check the `}<a parentName="p" {...{
        "href": "https://github.com/primer/primitives/releases/"
      }}>{`Primer Primitives`}</a>{` repository to follow along as we release new design tokens and systems.`}</p>
    <h3>{`Functional system`}</h3>
    <p>{`The functional system is based on the meaning, or purpose, that colors have in the interface. Use functional design tokens in designs and code to build product interfaces.`}</p>
    <p>{`By using Primer's functional color system you make sure that your interface:`}</p>
    <ul>
      <li parentName="ul">{`supports color modes out of the box`}</li>
      <li parentName="ul">{`gets all future themes updates "for free"`}</li>
      <li parentName="ul">{`uses `}<a parentName="li" {...{
          "href": "#combining-colors"
        }}>{`accessible color combination`}</a>{` across themes`}</li>
    </ul>
    <p>{`The functional system is structured into two groups of design tokens:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Foundations:`}</strong>{` Foregrounds (text and icons), backgrounds, and borders that make up most of a product interface.`}</li>
    </ul>
    <img width="960" alt="Dark gray, midtone gray and light gray swatches as example of foundational colors" src="https://user-images.githubusercontent.com/6951037/146926823-b8489e82-978d-4aed-b186-2dcc70691fc7.png" />
    <ul>
      <li parentName="ul"><strong parentName="li">{`Color roles:`}</strong>{` Foregrounds (text and icons), backgrounds, and borders that highlight affordance or the meaning of elements in the UI.`}</li>
    </ul>
    <img width="1200" alt="Accent: blue, Success: green, Attention: yellow, Danger: red, Severe: orange, Open: green, Closed: red, Done: purple" src="https://user-images.githubusercontent.com/378023/156498624-0e71a46b-9e1d-40c0-9f4a-18e37184b06e.png" />
    <h3>{`Foregrounds`}</h3>
    <p>{`Foreground elements are `}<strong parentName="p">{`text and icons`}</strong>{`. You can apply color to them by using any of the `}<inlineCode parentName="p">{`fg`}</inlineCode>{` (short for foreground) design tokens.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Foundations`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=fg.default",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.default`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Primary color for text and icons in any given interface. It should be used for body content, titles and labels.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=fg.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.muted`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use for content that is secondary or that provides additional context but is not critical to understanding the flow of an interface.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=fg.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.subtle`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use for placeholder text, icons or decorative foregrounds.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=fg.onEmphasis",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.onEmphasis`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`On emphasis is the text color designed to combine with `}<inlineCode parentName="td">{`.emphasis`}</inlineCode>{` backgrounds for optimal contrast.`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Color roles`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=accent.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.accent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use for interactive text or icons like links or buttons.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=success.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.success`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize a positive message.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=attention.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.attention`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight text or icons that require the user's attention.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=danger.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.danger`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize an error or a blocking status. Action is required.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=severe.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.severe`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize a level of severity between attention and danger.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=open.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.open`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to open tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=closed.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.closed`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to closed tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=done.fg",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`fg.done`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to completed tasks or workflows.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Backgrounds`}</h3>
    <p>{`Background colors apply to surfaces of components or UI elements, such as pages, boxes, and overlays.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Foundations`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=neutral.emphasisPlus",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.emphasis`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Provides the highest contrast against the default background, such as in tooltips.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=canvas.default",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.default`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Primary background color.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=canvas.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.subtle`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Provides visual rest and contrast against the default background.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=canvas.inset",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.inset`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Can be used instead of the default background to create a focal point, such as in conversations or activity feeds.`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Color roles`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=accent.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.accent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to accentuate interactive areas in the UI like selected elements.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=success.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.success`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight a positive message.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=attention.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.attention`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight elements that require a user's attention or pending statuses.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=danger.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.danger`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize an error or a blocking status, where action is required.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=severe.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.severe`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize an extra level of severity between attention and danger.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=open.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.open`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to open tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=closed.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.closed`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to closed tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=done.subtle",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.done`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to completed tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=accent.emphasis",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`bg.[ANY_OF_ABOVE].emphasis`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight the most important item of a view or an interface.`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Borders`}</h3>
    <p>{`Borders can be used to group content or to create a visible separation between sections or items. They’re most commonly used on tables, side sections, and cards.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Foundations`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=border.default",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.default`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to create bounds around content, for example elements inside a card. Default borders are critical to understanding a page layout.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=border.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.muted`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use for dividers to emphasize the separation between items, columns or sections.`}</td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Color roles`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=accent.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.accent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to accentuate interactive areas in the UI like selected elements.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=success.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.success`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight a positive message.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=attention.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.attention`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight elements that require a users attention or pending statuses.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=danger.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.danger`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize an error or a blocking status. Action is required.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=severe.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.severe`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to emphasize an extra level of severity between attention and danger.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=open.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.open`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to open tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=closed.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.closed`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to closed tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=done.muted",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.done`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to style text that refers to completed tasks or workflows.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><img parentName="td" {...{
              "src": "https://swatch-sid.vercel.app?mode=light&token=accent.emphasis",
              "alt": "\"\""
            }}></img>{` `}<inlineCode parentName="td">{`border.[ROLE].emphasis`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Use to highlight the most important item of a view or an interface.`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`Functional system in action`}</h2>
    <Box display="grid" gridTemplateColumns={['1fr', null, null, null, '1fr 1fr']} gridGap={5} mdxType="Box">
  <Box mdxType="Box">
    <img width="464" alt="Table component showing the combination of bg.subtle with border.default, and border.muted" src="https://user-images.githubusercontent.com/6951037/145223860-4db73d78-8791-44db-b94d-c7966ce13fab.png" />
    <Caption mdxType="Caption">Example of pairing foundation tokens: bg.subtle with border.default, and border.muted.</Caption>
  </Box>
  <Box mdxType="Box">
    <img width="464" alt="UI demonstration of what bg.inset looks like. It darkens the background of the whole UI" src="https://user-images.githubusercontent.com/6951037/145223862-2ea9027c-1474-47d0-8caf-8ab8d93a841b.png" />
    <Caption mdxType="Caption">
      Thinking in terms of elevation, bg.inset would represent an underground level. It's meant to create a feeling of
      focus or depth.
    </Caption>
  </Box>
    </Box>
    <Box display="grid" gridTemplateColumns={['1fr', null, null, null, '1fr 1fr']} gridGap={5} mdxType="Box">
  <Box mdxType="Box">
    <img width="464" alt="Example of a hover style on a list where the background uses bg.accent.emphasis and the text uses fg.onEmphasis." src="https://user-images.githubusercontent.com/6951037/141304060-1e3d5285-f80f-4363-8c07-e5865c6fe073.png" />
    <Caption mdxType="Caption">
      fg.onEmphasis pairs with bg.[ANY_COLOR_ROLE].emphasis tokens. This example shows fg.onEmphasis paired with
      bg.accent.emphasis.
    </Caption>
  </Box>
  <Box mdxType="Box">
    <img width="464" alt="Green success alert with check icon, pairing bg.success, fg.success, and border.success color tokens" src="https://user-images.githubusercontent.com/6951037/141304173-5f919074-b5ae-4c0b-b5d2-8a2cd6ea2d03.png" />
    <Caption mdxType="Caption">An example of an alert that pairs bg.success, fg.success, and border.success.</Caption>
  </Box>
    </Box>
    <h2>{`Combining colors`}</h2>
    <p>{`Not all colors pair well with each other. There are combinations of backgrounds and foregrounds that guarantee compliance with WCAG contrast guidelines and a wide range of hierarchical relationships between elements. Never use color on its own to convey a message or meaning. Pair it with explicit text and icons instead.`}</p>
    <h3>{`Pairing color roles`}</h3>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/6951037/146400758-3f487fe3-c07a-4b5c-b190-99f81f0efdb2.png" alt="" />
    <Caption mdxType="Caption">Pair color role foregrounds with their background counterparts or with bg.default and bg.subtle.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/6951037/146400887-e9340e29-688f-4320-b633-e9f02b93cde5.png" alt="" />
    <Caption mdxType="Caption">Never pair emphasis foregrounds with emphasis background.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Color roles and foregrounds`}</h3>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img src="https://user-images.githubusercontent.com/6951037/146401147-33bef1f2-a53c-4d06-b20d-3dc32eb1879f.png" alt="" />
    <Caption mdxType="Caption">Only use fg.muted with bg.default, bg.subtle, and bg.inset.</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img src="https://user-images.githubusercontent.com/6951037/146401229-60f13aa3-e3c4-4125-8879-829a769c7365.png" alt="" />
    <Caption mdxType="Caption">Never use fg.muted on bg.emphasis or any of the color roles backgrounds.</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Color roles recipes`}</h3>
    <img width="1200" alt="Combination of design tokens 1: bg.[COLOR-ROLE] + border.[COLOR-ROLE] + fg.[COLOR-ROLE]" src="https://user-images.githubusercontent.com/378023/156498633-d6e47cb1-64b4-46fb-90d6-65a5c4c6a2bc.png" />
    <img width="1200" alt="Combination of design tokens 2: bg.[COLOR-ROLE] + border.[COLOR-ROLE].emphasis + fg.[COLOR-ROLE]" src="https://user-images.githubusercontent.com/378023/156498642-f86a24b7-53ea-4b1e-ab47-0529587b08ef.png" />
    <img width="1200" alt="Combination of design tokens 3: bg.default + border.[COLOR-ROLE].emphasis + fg.[COLOR-ROLE]" src="https://user-images.githubusercontent.com/378023/156498649-e81a19bf-c94e-436e-9efc-258a1fd3be57.png" />
    <img width="1200" alt="Combination of design tokens 4: bg.[COLOR-ROLE] + border.[COLOR-ROLE] + fg.default" src="https://user-images.githubusercontent.com/378023/156498652-b1121c86-a670-4a92-9a8d-9db3c7e0ed9a.png" />
    <h2>{`Accessibility`}</h2>
    <p>{`When designing with colors consider the following to make your UI visually accessible.`}</p>
    <h3>{`Assure adequate color contrast`}</h3>
    <p>{`Color contrast between text and its background must meet `}<a parentName="p" {...{
        "href": "https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html"
      }}>{`required WCAG standards`}</a>{`.`}</p>
    <p>{`The contrast requirements are:`}</p>
    <ul>
      <li parentName="ul">{`4.5:1 for normal text`}</li>
      <li parentName="ul">{`3:1 for large text (>24px)`}</li>
      <li parentName="ul">{`3:1 for UI elements and graphics`}</li>
      <li parentName="ul">{`No contrast requirement for decorative and disabled elements`}</li>
    </ul>
    <p>{`Use an `}<a parentName="p" {...{
        "href": "https://webaim.org/resources/contrastchecker/"
      }}>{`online contrast checker`}</a>{` or a `}<a parentName="p" {...{
        "href": "/guides/accessibility/tools#contrast-plugin"
      }}>{`Figma plugin`}</a>{` to test your contrast.`}</p>
    <p>{`If you work with the `}<a parentName="p" {...{
        "href": "#color-roles-recipes"
      }}>{`functional colors`}</a>{`, this work has already been done for you. We checked all recommended combinations, so you are all set.`}</p>
    <h3>{`Don't rely on color alone`}</h3>
    <Box mb={3} display="flex" alignItems="flex-start" alignItems="center" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box as="p" mt="0" mdxType="Box">
    <Heading sx={{
          mt: 0,
          mb: 3,
          fontSize: 2
        }} as="h4" mdxType="Heading">
      Show state with more than color
    </Heading>
    Color vision deficiency is different for different people. To make sure everyone can understand and use your UI you should
    show state with more than a change in color. For example by using icons or changing the content.
  </Box>
  <img width="456" alt="Example icon that uses a distinct shape in addition to color to indicate an error." src="https://user-images.githubusercontent.com/813754/187186864-8a825434-978b-4e21-bac2-9c1235d75e2c.png" />
    </Box>
    <Box mb={3} display="flex" alignItems="flex-start" alignItems="start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box as="p" mt="0" mdxType="Box">
    <Heading sx={{
          mt: 0,
          mb: 3,
          fontSize: 2
        }} as="h4" mdxType="Heading">
      Connect labels to graphs with lines or patterns
    </Heading>
    For charts and graphs you can position the labels on top or close to each section. You can also use patterns to distinguish
    different parts.
  </Box>
  <img width="456" alt="Two graphs. One is a line graph and one is a pie graph. The line graph is demonstrating using unique shapes for each comparison line's plot symbol. The pie chart is demonstrating how each slice's label can use a line to point to its corresponding slide." src="https://user-images.githubusercontent.com/813754/187186931-84bcc541-7cea-45f2-a686-21c5428e0620.png" />
    </Box>
    <h2>{`Color outside the functional system`}</h2>
    <p>{`Primer does not currently provide a presentational or data visualization color system. If you or your team at GitHub are working on a feature that has color needs beyond the functional system, please open an issue in the `}<a parentName="p" {...{
        "href": "https://github.com/github/primer/issues"
      }}>{`github/primer`}</a>{` repository (only available to GitHub staff).`}</p>
    <h2>{`How to use colors in Primer libraries?`}</h2>
    <p>{`Primer colors exist in different formats and are made available throughout the Primer libraries and tools. Not all colors exist everywhere and the naming depends on the Primer library. Below is a list to help find the right Primer color documentation that is specific to that role and environment.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`I am`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Documentation`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Example color usage`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`A product `}<strong parentName="td">{`designer`}</strong>{` working in `}<strong parentName="td">{`Figma`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://www.figma.com/file/B5XPE8IwGPIZDAvN7jqWqx/Primer-Primitives"
            }}>{`Primer Primitives`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`bg/accent`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`An `}<strong parentName="td">{`engineer`}</strong>{` using `}<strong parentName="td">{`Primer ViewComponents`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://primer.style/view-components/system-arguments#color"
            }}>{`color system arguments`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`bg: :accent`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`An `}<strong parentName="td">{`engineer`}</strong>{` using `}<strong parentName="td">{`Primer React`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://primer.style/react/overriding-styles"
            }}>{`sx props`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`accent.subtle`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`An `}<strong parentName="td">{`engineer`}</strong>{` creating `}<strong parentName="td">{`custom UI`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://primer.style/css/utilities/colors"
            }}>{`Primer CSS color utilities`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`color-bg-accent`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`A Primer `}<strong parentName="td">{`React maintainer`}</strong>{` creating a `}<strong parentName="td">{`component`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://primer.style/primitives/colors"
            }}>{`Primer Primitives js properties`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`accent.subtle`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`A Primer `}<strong parentName="td">{`CSS maintainer`}</strong>{` creating a `}<strong parentName="td">{`component`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://primer.style/primitives/colors"
            }}>{`Primer Primitives css variables`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`--color-accent-subtle`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`Stuck choosing the right color? Feel free to reach out in the `}<a parentName="p" {...{
        "href": "https://github.slack.com/archives/CSGAVNZ19"
      }}>{`#primer`}</a>{` Slack channel.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      